import { TWO_PI } from "./constants";
import { Vector3d } from "./vector3d";
import type { OrbitalElements } from "./orbital_elements";
import { meanAnomalyAt, semiLatusRectum } from "./orbital_elements";
import type { StateVector } from "./state_vector";
import { perifocalToEcliptic } from "./coordinate_transforms";

const MAX_KEPLER_ITERATIONS = 50;
const KEPLER_TOLERANCE = 1e-12;

/**
 * Solve Kepler's equation for eccentric/hyperbolic anomaly using Newton-Raphson.
 *
 * Elliptical (e < 1):   M = E - e*sin(E)
 * Hyperbolic (e >= 1):  M = e*sinh(H) - H
 *
 * @param M Mean anomaly (radians)
 * @param e Eccentricity
 * @returns Eccentric anomaly E (elliptical) or hyperbolic anomaly H
 */
export function solveKeplerEquation(M: number, e: number): number {
  if (e < 1.0) {
    // Elliptical: M = E - e*sin(E)
    M = M % TWO_PI;
    if (M < 0) M += TWO_PI;

    let E = e < 0.8 ? M : Math.PI;
    for (let i = 0; i < MAX_KEPLER_ITERATIONS; i++) {
      const dE = (E - e * Math.sin(E) - M) / (1.0 - e * Math.cos(E));
      E -= dE;
      if (Math.abs(dE) < KEPLER_TOLERANCE) return E;
    }
    return E;
  } else {
    // Hyperbolic: M = e*sinh(H) - H
    let H = M; // Initial guess
    for (let i = 0; i < MAX_KEPLER_ITERATIONS; i++) {
      const dH = (e * Math.sinh(H) - H - M) / (e * Math.cosh(H) - 1.0);
      H -= dH;
      if (Math.abs(dH) < KEPLER_TOLERANCE) return H;
    }
    return H;
  }
}

/**
 * Convert eccentric/hyperbolic anomaly to true anomaly.
 *
 * Elliptical:  tan(nu/2) = sqrt((1+e)/(1-e)) * tan(E/2)
 * Hyperbolic:  tan(nu/2) = sqrt((e+1)/(e-1)) * tanh(H/2)
 */
export function eccentricAnomalyToTrueAnomaly(E: number, e: number): number {
  if (e < 1.0) {
    const sinNuOver2 = Math.sqrt(1.0 + e) * Math.sin(E / 2.0);
    const cosNuOver2 = Math.sqrt(1.0 - e) * Math.cos(E / 2.0);
    return 2.0 * Math.atan2(sinNuOver2, cosNuOver2);
  } else {
    // Hyperbolic
    return 2.0 * Math.atan(Math.sqrt((e + 1.0) / (e - 1.0)) * Math.tanh(E / 2.0));
  }
}

/**
 * Convert true anomaly to eccentric/hyperbolic anomaly.
 */
export function trueAnomalyToEccentricAnomaly(nu: number, e: number): number {
  if (e < 1.0) {
    return 2.0 * Math.atan2(
      Math.sqrt(1.0 - e) * Math.sin(nu / 2.0),
      Math.sqrt(1.0 + e) * Math.cos(nu / 2.0),
    );
  } else {
    // Hyperbolic: H = 2*atanh(sqrt((e-1)/(e+1)) * tan(nu/2))
    const tanNuOver2 = Math.tan(nu / 2.0);
    const val = Math.sqrt((e - 1.0) / (e + 1.0)) * tanNuOver2;
    return 2.0 * Math.atanh(clamp(val, -0.99999999, 0.99999999));
  }
}

/**
 * Convert eccentric/hyperbolic anomaly to mean anomaly.
 *
 * Elliptical:  M = E - e*sin(E)
 * Hyperbolic:  M = e*sinh(H) - H
 */
export function eccentricAnomalyToMeanAnomaly(E: number, e: number): number {
  if (e < 1.0) {
    return E - e * Math.sin(E);
  } else {
    return e * Math.sinh(E) - E;
  }
}

/**
 * Orbital radius at a given true anomaly.
 * r = a(1 - e^2) / (1 + e*cos(nu))
 *
 * This is the equation of the orbit in polar form (conic section).
 *
 * @param a  Semi-major axis (km)
 * @param e  Eccentricity
 * @param nu True anomaly (radians)
 * @returns Distance from central body (km)
 */
export function radiusAtTrueAnomaly(a: number, e: number, nu: number): number {
  return a * (1.0 - e * e) / (1.0 + e * Math.cos(nu));
}

/**
 * Convert Keplerian orbital elements to a Cartesian state vector
 * (position and velocity) at a given time.
 *
 * Algorithm (spec section 3.3, steps 1-6):
 * 1. Propagate mean anomaly to target time
 * 2. Solve Kepler's equation for eccentric anomaly
 * 3. Convert to true anomaly
 * 4. Compute orbital radius
 * 5. Build position and velocity in the perifocal frame
 * 6. Rotate from perifocal to ecliptic frame
 *
 * @param elements Orbital elements
 * @param gm       Gravitational parameter of central body (km^3/s^2)
 * @param jd       Julian Date at which to compute state
 * @returns State vector (position in km, velocity in km/s)
 */
export function elementsToState(
  elements: OrbitalElements,
  gm: number,
  jd: number,
): StateVector {
  // Step 1: Mean anomaly at time jd
  const M = meanAnomalyAt(elements, jd, gm);

  // Step 2: Solve Kepler's equation
  const E = solveKeplerEquation(M, elements.eccentricity);

  // Step 3: True anomaly
  const nu = eccentricAnomalyToTrueAnomaly(E, elements.eccentricity);

  // Step 4: Orbital radius
  const r = radiusAtTrueAnomaly(elements.semiMajorAxis, elements.eccentricity, nu);

  // Step 5: Position and velocity in perifocal (orbital plane) frame
  const cosNu = Math.cos(nu);
  const sinNu = Math.sin(nu);
  const xOrb = r * cosNu;
  const yOrb = r * sinNu;

  const p = semiLatusRectum(elements);
  const sqrtMuOverP = Math.sqrt(gm / p);
  const vxOrb = -sqrtMuOverP * sinNu;
  const vyOrb = sqrtMuOverP * (elements.eccentricity + cosNu);

  const posPerifocal = new Vector3d(xOrb, yOrb, 0);
  const velPerifocal = new Vector3d(vxOrb, vyOrb, 0);

  // Step 6: Rotate to ecliptic frame
  const position = perifocalToEcliptic(
    posPerifocal,
    elements.longitudeOfAscendingNode,
    elements.inclination,
    elements.argumentOfPeriapsis,
  );

  const velocity = perifocalToEcliptic(
    velPerifocal,
    elements.longitudeOfAscendingNode,
    elements.inclination,
    elements.argumentOfPeriapsis,
  );

  return {
    position,
    velocity,
    time: jd,
    centralBody: elements.centralBody,
  };
}

/**
 * Convert a Cartesian state vector to Keplerian orbital elements.
 *
 * Algorithm (spec section 4.3, steps 1-10):
 * 1. Specific angular momentum h = r x v
 * 2. Node vector n = k x h
 * 3. Eccentricity vector
 * 4-6. Eccentricity, specific energy, semi-major axis
 * 7-9. Inclination, RAAN, argument of periapsis
 * 10. True anomaly, then convert to mean anomaly at epoch
 *
 * @param state State vector (position in km, velocity in km/s)
 * @param gm    Gravitational parameter (km^3/s^2)
 * @returns Orbital elements
 */
export function stateToElements(state: StateVector, gm: number): OrbitalElements {
  const r = state.position;
  const v = state.velocity;
  const rMag = r.magnitude;
  const vMag = v.magnitude;

  // Step 1: Specific angular momentum
  const h = Vector3d.cross(r, v);
  const hMag = h.magnitude;

  // Step 2: Node vector (k x h where k = [0,0,1])
  const n = new Vector3d(-h.y, h.x, 0);
  const nMag = n.magnitude;

  // Step 3: Eccentricity vector e = ((v^2 - GM/r)*r - (r.v)*v) / GM
  const rv = Vector3d.dot(r, v);
  const eVec = r.scale(vMag * vMag - gm / rMag).subtract(v.scale(rv)).divideBy(gm);

  // Step 4: Eccentricity
  const e = eVec.magnitude;

  // Step 5: Specific energy
  const xi = (vMag * vMag) / 2.0 - gm / rMag;

  // Step 6: Semi-major axis
  let a: number;
  if (Math.abs(1.0 - e) < 1e-10) {
    a = Infinity; // Parabolic
  } else {
    a = -gm / (2.0 * xi);
  }

  // Step 7: Inclination
  const inc = Math.acos(clamp(h.z / hMag, -1.0, 1.0));

  // Step 8: Longitude of ascending node
  let lan: number;
  if (nMag < 1e-15) {
    lan = 0; // Equatorial orbit
  } else {
    lan = Math.acos(clamp(n.x / nMag, -1.0, 1.0));
    if (n.y < 0) lan = TWO_PI - lan;
  }

  // Step 9: Argument of periapsis
  let aop: number;
  if (nMag < 1e-15 || e < 1e-10) {
    if (e < 1e-10) {
      aop = 0; // Circular orbit
    } else {
      // Equatorial orbit with eccentricity: use longitude of periapsis
      aop = Math.atan2(eVec.y, eVec.x);
      if (aop < 0) aop += TWO_PI;
    }
  } else {
    aop = Math.acos(clamp(Vector3d.dot(n, eVec) / (nMag * e), -1.0, 1.0));
    if (eVec.z < 0) aop = TWO_PI - aop;
  }

  // Step 10: True anomaly
  let nu: number;
  if (e < 1e-10) {
    // Circular orbit: use argument of latitude
    if (nMag < 1e-15) {
      nu = Math.atan2(r.y, r.x);
    } else {
      nu = Math.acos(clamp(Vector3d.dot(n, r) / (nMag * rMag), -1.0, 1.0));
      if (r.z < 0) nu = TWO_PI - nu;
    }
  } else {
    nu = Math.acos(clamp(Vector3d.dot(eVec, r) / (e * rMag), -1.0, 1.0));
    if (rv < 0) nu = TWO_PI - nu;
  }

  // Convert true anomaly -> eccentric anomaly -> mean anomaly
  const E = trueAnomalyToEccentricAnomaly(nu, e);
  let M0 = eccentricAnomalyToMeanAnomaly(E, e);
  if (e < 1.0 && M0 < 0) M0 += TWO_PI;

  return {
    semiMajorAxis: a,
    eccentricity: e,
    inclination: inc,
    longitudeOfAscendingNode: lan,
    argumentOfPeriapsis: aop,
    meanAnomalyAtEpoch: M0,
    epoch: state.time,
    centralBody: state.centralBody,
  };
}

/**
 * Vis-viva equation: v = sqrt(GM * (2/r - 1/a))
 *
 * Gives the orbital speed at distance r from the central body for an
 * orbit with semi-major axis a. Fundamental energy conservation relation.
 *
 * @param gm Gravitational parameter (km^3/s^2)
 * @param r  Distance from central body (km)
 * @param a  Semi-major axis (km)
 * @returns Orbital speed (km/s)
 */
export function visViva(gm: number, r: number, a: number): number {
  return Math.sqrt(gm * (2.0 / r - 1.0 / a));
}

/**
 * Circular orbital velocity at a given radius.
 * v_circ = sqrt(GM / r)
 *
 * @param gm Gravitational parameter (km^3/s^2)
 * @param r  Orbital radius (km)
 * @returns Circular velocity (km/s)
 */
export function circularVelocity(gm: number, r: number): number {
  return Math.sqrt(gm / r);
}

/**
 * Escape velocity at a given radius.
 * v_esc = sqrt(2 * GM / r)
 *
 * @param gm Gravitational parameter (km^3/s^2)
 * @param r  Distance from central body (km)
 * @returns Escape velocity (km/s)
 */
export function escapeVelocity(gm: number, r: number): number {
  return Math.sqrt(2.0 * gm / r);
}

/**
 * Orbital period from semi-major axis.
 * T = 2*pi * sqrt(a^3 / GM)
 *
 * @param a  Semi-major axis (km)
 * @param gm Gravitational parameter (km^3/s^2)
 * @returns Period (seconds)
 */
export function orbitalPeriod(a: number, gm: number): number {
  return TWO_PI * Math.sqrt(a * a * a / gm);
}

/**
 * Mean motion (angular rate of mean anomaly advance).
 * n = sqrt(GM / a^3)
 *
 * @param a  Semi-major axis (km)
 * @param gm Gravitational parameter (km^3/s^2)
 * @returns Mean motion (radians/second)
 */
export function meanMotion(a: number, gm: number): number {
  return Math.sqrt(gm / (a * a * a));
}

/**
 * Compute position in ecliptic frame at a given true anomaly.
 * Useful for generating orbit path geometry without propagating in time.
 *
 * @param elements Orbital elements
 * @param nu       True anomaly (radians)
 * @returns Position in ecliptic frame (km)
 */
export function positionAtTrueAnomaly(elements: OrbitalElements, nu: number): Vector3d {
  const r = radiusAtTrueAnomaly(elements.semiMajorAxis, elements.eccentricity, nu);
  const cosNu = Math.cos(nu);
  const sinNu = Math.sin(nu);
  const posPerifocal = new Vector3d(r * cosNu, r * sinNu, 0);

  return perifocalToEcliptic(
    posPerifocal,
    elements.longitudeOfAscendingNode,
    elements.inclination,
    elements.argumentOfPeriapsis,
  );
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
